/**
 * Starting a piece of work.
 *
 * Not "create a branch". You pick an issue or describe the work, and the
 * branch, the working database and eventually the merge request all follow
 * from it. The branch name is shown before anything is created, so there is no
 * surprise in what appears in GitLab.
 *
 * Two tabs rather than one form with a mode switch: picking existing work and
 * describing new work are different actions with different fields, and a form
 * that changes shape under a radio button reads as one thing that cannot make
 * up its mind.
 *
 * Who the work is for (issue #67) is shown, never required. The issue list can
 * be narrowed to what is assigned to you or to what you were asked to review,
 * and new work carries an "Assign to" field. BEST encodes no assignment
 * policy: it shows what GitLab holds, GitLab enforces, and no control here is
 * disabled because of who was or was not chosen. The one default is a
 * convenience - new work is assigned to whoever raises it - and it can be
 * changed or cleared in the field.
 */

import { useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import Link from '@mui/material/Link'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Typography from '@mui/material/Typography'
import { ApiError, api } from '../../api'
import type { IssueRow, PersonMergeHint } from '../../schema'
import { useProject } from '../../state/Project'
import { useMembers } from './members'
import PeoplePicker from './PeoplePicker'

/**
 * Which issues the Open issues tab lists. Each maps to ONE server flag (or
 * none), never two: the server refuses `mine` together with `review`, and they
 * are different questions - what is yours, what is waiting on you.
 */
type Scope = 'all' | 'mine' | 'review'

const NOTHING: Record<Exclude<Scope, 'all'>, string> = {
  mine: 'Nothing is assigned to you.',
  review: 'Nobody has asked you for a review.',
}

export default function StartWorkDialog({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  const { current, reload } = useProject()
  const slug = current?.slug ?? ''

  const [tab, setTab] = useState(0)
  const [issues, setIssues] = useState<IssueRow[] | null>(null)
  const [search, setSearch] = useState('')
  const [scope, setScope] = useState<Scope>('all')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  // undefined = not touched, so the default (you) applies once the people have
  // loaded. null = deliberately cleared, which is NOT the same as untouched: it
  // is sent as an explicit null. Collapsing the two would make "I cleared it"
  // and "I never looked" indistinguishable.
  const [assignee, setAssignee] = useState<PersonMergeHint | null | undefined>(undefined)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  // Wanted only on the tab that has the field, and kept once read.
  const members = useMembers(slug, open && tab === 1)
  const you = members.status === 'ready' ? members.data.me : null
  const assignedTo = assignee === undefined ? you : assignee

  useEffect(() => {
    if (!open || !slug) return
    let live = true
    setError('')
    setIssues(null)
    api
      // One flag at most, from the one toggle that can be on.
      .issues(slug, { search, mine: scope === 'mine', review: scope === 'review' })
      // A slower answer for the previous toggle must not land under this one:
      // someone's review list under "Assigned to me" is a wrong answer that
      // looks right.
      .then((payload) => live && setIssues(payload.items))
      .catch((failure: ApiError) => live && setError(failure.message))
    return () => {
      live = false
    }
  }, [open, slug, search, scope])

  const begin = async (body: Parameters<typeof api.startWork>[1]) => {
    setBusy(true)
    setError('')
    try {
      await api.startWork(slug, body)
      // The branch, the model and where you left off all just changed.
      await reload()
      setTitle('')
      setDescription('')
      setAssignee(undefined)
      onClose()
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : 'Could not start work.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Start work</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 1 }}>
          BEST creates a branch from the issue and imports the model into it.
          Your saves become commits on that branch.
        </DialogContentText>

        <Tabs value={tab} onChange={(_event, next) => setTab(next)} sx={{ mb: 2 }}>
          <Tab label="Open issues" />
          <Tab label="New work" />
        </Tabs>

        {tab === 0 ? (
          <>
            <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={scope}
                aria-label="Which issues"
                // Clicking the chosen button reports null; a list with nothing
                // chosen is not a state, so it is ignored.
                onChange={(_event, next: Scope | null) => next && setScope(next)}
              >
                <ToggleButton value="all">All</ToggleButton>
                <ToggleButton value="mine">Assigned to me</ToggleButton>
                <ToggleButton value="review">Review requested</ToggleButton>
              </ToggleButtonGroup>
              <TextField
                label="Search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                size="small"
                sx={{ flexGrow: 1 }}
              />
            </Stack>
            {issues === null && !error ? (
              <Stack direction="row" spacing={2} sx={{ alignItems: 'center', py: 3 }}>
                <CircularProgress size={20} />
                <Typography color="text.secondary">Reading the issues…</Typography>
              </Stack>
            ) : null}
            {issues && issues.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 3 }}>
                {scope === 'all' ? (
                  <>
                    No open model-change issues for this program
                    {search ? ' matching that' : ''}. Describe the work on the next
                    tab and BEST will raise one.
                  </>
                ) : (
                  <>
                    {NOTHING[scope]}
                    {search ? ' Nothing matches that search.' : ''}
                  </>
                )}
              </Typography>
            ) : null}
            <List dense>
              {(issues ?? []).map((issue) => (
                <ListItemButton
                  key={issue.iid}
                  disabled={busy}
                  onClick={() => void begin({ issue: issue.iid })}
                >
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        <Typography sx={{ fontWeight: 600 }}>#{issue.iid}</Typography>
                        <span>{issue.title}</span>
                        {issue.labels.map((label) => (
                          <Chip key={label} label={label} size="small" />
                        ))}
                      </Stack>
                    }
                    // A div, not the default <p>: the secondary text holds blocks.
                    slotProps={{ secondary: { component: 'div' } }}
                    secondary={
                      <>
                        {issue.branch_name}
                        {issue.assignees?.length ? (
                          <Box component="span" sx={{ display: 'block' }}>
                            Assigned to {issue.assignees.map((name) => `@${name}`).join(', ')}
                          </Box>
                        ) : null}
                        {issue.merge_request ? (
                          <Box component="span" sx={{ display: 'block' }}>
                            <Link
                              href={issue.merge_request.web_url}
                              target="_blank"
                              rel="noreferrer"
                              // The row starts work; the link only goes to the
                              // merge request. Without this one click does both.
                              onClick={(event) => event.stopPropagation()}
                            >
                              !{issue.merge_request.iid} {issue.merge_request.title}
                            </Link>
                            {issue.merge_request.source ? ` from ${issue.merge_request.source}` : ''}
                          </Box>
                        ) : null}
                        {/* Issue #98: a merge request opened from GitLab's
                            issue page onto an empty copy of the branch reads
                            as Draft, not as empty. Starting work here still
                            opens the issue's own branch, where the work is. */}
                        {issue.merge_request?.empty ? (
                          <Box component="span" sx={{ display: 'block', color: 'warning.main' }}>
                            It has no commits, so there is nothing in it to review.
                          </Box>
                        ) : null}
                      </>
                    }
                  />
                </ListItemButton>
              ))}
            </List>
          </>
        ) : (
          <Stack spacing={2}>
            <TextField
              label="What are you doing?"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Refine the TBRs on SPS"
              autoFocus
              fullWidth
            />
            <TextField
              label="Detail"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              multiline
              minRows={3}
              fullWidth
            />
            <PeoplePicker
              label="Assign to"
              members={members}
              value={assignedTo}
              onChange={setAssignee}
              helperText="Left empty, it is assigned to you."
            />
            <Typography variant="body2" color="text.secondary">
              Raises an issue in GitLab and branches from it.
            </Typography>
          </Stack>
        )}

        {error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            <AlertTitle>Could not start</AlertTitle>
            <Box sx={{ whiteSpace: 'pre-line' }}>{error}</Box>
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        {tab === 1 ? (
          <Button
            onClick={() =>
              void begin({
                title,
                description,
                // Sent only when there is something to say. Until the people
                // load, or if they never do, the server's own default (the
                // caller) is the answer, so the key is left out rather than
                // guessed. A cleared field is an explicit null.
                ...(assignee !== undefined || you ? { assignee_id: assignedTo?.id ?? null } : {}),
              })
            }
            variant="contained"
            disabled={busy || !title.trim()}
          >
            {busy ? 'Starting…' : 'Raise issue and start'}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  )
}
